import {useTableSort} from '@flexinet/ui-components';
import {DataTableSortStatus} from 'mantine-datatable';

import {useExecutionsQuery} from './useExecutionsQuery';
import {useExecutionsSearch} from './useExecutionsSearch';
import {useExecutionsTableColumns} from './useExecutionsTableColumns';
import {Execution} from '../types';

// DEFAULT: newest first, keyed on `created`.
//
// Executions are a time-ordered log, so the operator's question is almost
// always "what ran most recently?" — the default has to answer that on first
// paint.
//
// `startedAt` is the tempting key, because it is what an operator calls the
// time an execution ran, and the table shows it. It is the WRONG default:
// `startedAt` is null until the state machine actually begins, so every PENDING
// execution — the just-submitted ones, the most interesting rows on the page —
// would sort to the bottom under the nulls-last rule. A queued execution
// disappearing below the fold is precisely the failure this default must avoid.
//
// `created` is set when the record is written and never changes afterwards, so
// it covers PENDING rows too. It is nullable on the Execution type like every
// other field here, which the library's guidance would rather it were not, but
// the only non-null field is `id` and ordering executions by an opaque id is
// meaningless to a reader. `listExecutions` already returns created-descending,
// so this default AGREES with the server and the first paint does not reshuffle
// what came back.
//
// Never `updated`: it moves under someone reading the table as phases complete.
//
// If Execution.created is ever made non-null in the workflows-api schema, this
// comment is the only thing that needs to change.
const DEFAULT_SORT_STATUS: DataTableSortStatus<Execution> = {
    columnAccessor: 'created',
    direction: 'desc',
};

export const useExecutions = (filters: Record<string, string> = {}) => {
    const query = useExecutionsQuery(filters);
    const {keyword} = useExecutionsSearch();
    const {columns} = useExecutionsTableColumns();

    // Type guard rather than a bare flatMap: a page that failed or is still
    // in flight is not shaped like a page, and spreading it would put
    // `undefined` into the records array.
    const items: Execution[] = query.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) ?? [];

    // ⚠️ Both the keyword filter and the sort below cover only the pages
    // FETCHED SO FAR. Under token pagination the browser cannot filter or
    // reorder rows it has never seen. That is a property of the model, not a
    // bug: it is why the footer reports a LOADED count rather than a total, and
    // why `unfilteredCount` is passed alongside the match count so the operator
    // can see the search was scoped to a window.
    const needle = keyword.trim().toLowerCase();

    const matched = items.filter(item =>
        !needle ||
        item.name?.toLowerCase().includes(needle) ||
        item.id?.toLowerCase().includes(needle) ||
        item.executionStatus?.toLowerCase().includes(needle) ||
        item.createdByUsername?.toLowerCase().includes(needle) ||
        item.playbook?.displayName?.toLowerCase().includes(needle) ||
        item.playbook?.name?.toLowerCase().includes(needle)
    );

    // Sort state is component-level, not the module-scoped store: sort is how
    // ONE operator is viewing the table right now, unlike the keyword and the
    // filters, which are shared with sibling components.
    const {
        sortStatus,
        setSortStatus,
        sorted: visibleItems,
    } = useTableSort<Execution>(matched, DEFAULT_SORT_STATUS);

    // hasNextPage is checked HERE so no call site can fire a fetch for a page
    // that does not exist.
    const fetchNextPage = () => {
        if (query.hasNextPage) {
            query.fetchNextPage();
        }
    };

    return {
        items,
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        unfilteredCount: items.length,
        hasNextPage: !!query.hasNextPage,
        // The control only needs to know whether more exist, never the token's
        // value — a nextToken is opaque to the client.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchNextPage,
    };
};
